import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {useATProtoAgent} from "@/app/atproto";
import {logger} from "@/app/events";
import {listLibraryEntries} from "@/app/library";
import {db, type LocalLibraryEntry, type LocalStudySession} from "@/app/storage";
import {listStudySessions} from "@/app/study";

const SYNC_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes

interface SyncContextValue {
  isSyncing: boolean;
  error: Error | null;
  syncedCount: {library: number; sessions: number};
  runSync: (force?: boolean) => Promise<void>;
}

const SyncContext = createContext<SyncContextValue | undefined>(undefined);

export function useSync() {
  const context = useContext(SyncContext);
  if (!context) {
    throw new Error("useSync must be used within a SyncProvider");
  }
  return context;
}

export function SyncProvider(props: {children: React.ReactNode}) {
  const agent = useATProtoAgent();

  const syncAttempted = useRef(false);

  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [syncedCount, setSyncedCount] = useState({library: 0, sessions: 0});

  const syncLibrary = useCallback(async () => {
    let cursor: string | undefined;
    let count = 0;

    // Fetch all library entries
    do {
      const response = await listLibraryEntries(agent, {cursor, limit: 100});
      cursor = response.cursor;

      const entries: LocalLibraryEntry[] = response.records.map(r => ({
        ...r.value,
        uri: r.uri,
        cid: r.cid,
        synced: true,
        isDeleted: 0,
      }));

      if (entries.length > 0) {
        // Upsert: Overwrite existing entries with same URI
        await db.libraryEntries.bulkPut(entries);
        count += entries.length;
      }
    } while (cursor);

    return count;
  }, [agent]);

  const syncSessions = useCallback(async () => {
    let cursor: string | undefined;
    let count = 0;

    // Fetch all study sessions
    do {
      const response = await listStudySessions(agent, {cursor, limit: 100});
      cursor = response.cursor;

      const sessions: LocalStudySession[] = response.records.map(r => ({
        ...r.value,
        uri: r.uri,
        cid: r.cid,
        synced: true,
      }));

      if (sessions.length > 0) {
        // Upsert
        await db.studySessions.bulkPut(sessions);
        count += sessions.length;
      }
    } while (cursor);

    return count;
  }, [agent]);

  const runSync = useCallback(
    async (force = false) => {
      if (isSyncing) return;

      const lastSync = localStorage.getItem("last-sync-timestamp");
      const now = Date.now();

      if (!force && lastSync && now - parseInt(lastSync, 10) < SYNC_COOLDOWN_MS) {
        logger.storage.debug("Skipping sync due to cooldown");
        return;
      }

      try {
        setIsSyncing(true);
        setError(null);
        logger.storage.info("Starting sync from PDS...");

        const [libCount, sessCount] = await Promise.all([syncLibrary(), syncSessions()]);

        setSyncedCount({library: libCount, sessions: sessCount});
        localStorage.setItem("last-sync-timestamp", now.toString());
        logger.storage.info(
          `Sync complete. Processed ${libCount} entries and ${sessCount} sessions.`,
        );
      } catch (err) {
        logger.storage.error("Sync failed", {error: err});
        setError(err as Error);
      } finally {
        setIsSyncing(false);
      }
    },
    [isSyncing, syncLibrary, syncSessions],
  );

  // Initial auto-sync
  useEffect(() => {
    if (!syncAttempted.current) {
      syncAttempted.current = true;
      runSync();
    }
  }, [runSync]);

  const value = useMemo(
    () => ({
      isSyncing,
      error,
      syncedCount,
      runSync,
    }),
    [isSyncing, error, syncedCount, runSync],
  );

  return <SyncContext.Provider value={value}>{props.children}</SyncContext.Provider>;
}
